import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

import { Button, Checkbox } from '@cargomatic/ui';
import {
  formatCommittmentCell,
  formatCreatedAtCell,
  formatCustomerCell,
  formatCustomerRateConditionCell,
  formatCustomerTypeCell,
  formatEquipmenSizeRateConditionCell,
  formatEquipmenTypeRateConditionCell,
  formatHazardousRateConditionCell,
  formatOrderTypeRateConditionCell,
  formatRateCell,
  formatReversibleCell,
  formatSequenceCell,
  formatOriginCell,
  formatDestinationCell,
  formatStatusCell,
  formatSteamshipLineRateConditionCell,
  formatUpdatedAtCell,
  formatValidityEnd,
  formatValidityStart,
} from './formatters';

import { RATE_CONDITION_COLUMN_PREFIX } from 'constants/LaneRates';

const SortableColumn = ({ children, column }) => {
  const sortDir = column.getIsSorted();
  return (
    <Button
      variant="ghost"
      className="px-0"
      onClick={() => column.toggleSorting(sortDir === 'asc')}
    >
      {children}
      {!sortDir && <ArrowUpDown className="ml-2 h-4 w-4" />}
      {sortDir === 'asc' && <ArrowUp className="ml-2 h-4 w-4" />}
      {sortDir === 'desc' && <ArrowDown className="ml-2 h-4 w-4" />}
    </Button>
  );
};

export const getColumns = () => {
  const { t } = useTranslation();

  return [
    {
      accessorKey: 'customerId',
      header: ({ table }) => {
        let checked;
        if (table.getIsAllPageRowsSelected()) {
          checked = true;
        } else if (table.getIsSomePageRowsSelected()) {
          checked = 'indeterminate';
        } else {
          checked = false;
        }
        return (
          <Checkbox
            checked={checked}
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'customerType',
      header: ({ column }) => {
        return (
          <SortableColumn column={column}>{t('CUSTOMER_TYPE')}</SortableColumn>
        );
      },
      cell: formatCustomerTypeCell,
    },

    {
      id: 'sequenceDisplay',
      header: () => t('SEQUENCE'),
      cell: formatSequenceCell,
    },
    {
      accessorKey: 'customerName',
      header: ({ column }) => {
        return (
          <div className="min-w-48">
            <SortableColumn column={column}>{t('CUSTOMER')}</SortableColumn>
          </div>
        );
      },
      cell: formatCustomerCell,
    },
    {
      accessorKey: 'origin',
      header: ({ column }) => {
        return (
          <div className="min-w-32">
            <SortableColumn column={column}>{t('ORIGIN')}</SortableColumn>
          </div>
        );
      },
      cell: formatOriginCell,
    },
    {
      accessorKey: 'destination',
      header: ({ column }) => {
        return (
          <div className="min-w-32">
            <SortableColumn column={column}>{t('DESTINATION')}</SortableColumn>
          </div>
        );
      },
      cell: formatDestinationCell,
    },
    {
      accessorKey: 'rate',
      header: ({ column }) => {
        return (
          <div className="flex justify-end">
            <SortableColumn column={column} className="text-right">
              {t('RATE')}
            </SortableColumn>
          </div>
        );
      },
      cell: formatRateCell,
    },

    {
      id: `${RATE_CONDITION_COLUMN_PREFIX}Customer`,
      header: () => <div className="w-36">{t('CUSTOMER')}</div>,
      cell: formatCustomerRateConditionCell,
    },
    {
      id: `${RATE_CONDITION_COLUMN_PREFIX}EquipmentType`,
      header: () => t('EQUIPMENT'),
      cell: formatEquipmenTypeRateConditionCell,
    },
    {
      id: `${RATE_CONDITION_COLUMN_PREFIX}EquipmentSize`,
      header: () => (
        <div className="text-right min-w-20">{t('EQUIP_SIZE')}</div>
      ),
      cell: formatEquipmenSizeRateConditionCell,
    },
    {
      id: `${RATE_CONDITION_COLUMN_PREFIX}Hazardous`,
      header: () => t('HAZARDOUS'),
      cell: formatHazardousRateConditionCell,
    },
    {
      id: `${RATE_CONDITION_COLUMN_PREFIX}OrderType`,
      header: () => <div className="min-w-20">{t('ORDER_TYPE')}</div>,
      cell: formatOrderTypeRateConditionCell,
    },
    {
      accessorKey: `${RATE_CONDITION_COLUMN_PREFIX}SteamshipOrRailroad`,
      header: () => t('STEAMSHIP_RAILROAD'),
      cell: formatSteamshipLineRateConditionCell,
    },
    {
      accessorKey: 'reversible',
      header: () => <div className="text-right">{t('REVERSIBLE')}?</div>,
      cell: formatReversibleCell,
    },
    {
      accessorKey: 'commitment',
      header: () => <div className="text-right">{t('COMMITMENT')}</div>,
      cell: formatCommittmentCell,
    },
    {
      accessorKey: 'effectiveStart',
      header: ({ column }) => {
        return (
          <SortableColumn column={column} className="min-w-24">
            {t('VALIDITY_START')}
          </SortableColumn>
        );
      },
      cell: formatValidityStart,
    },
    {
      accessorKey: 'effectiveEnd',
      header: ({ column }) => {
        return (
          <SortableColumn column={column} className="min-w-24">
            {t('VALIDITY_END')}
          </SortableColumn>
        );
      },
      cell: formatValidityEnd,
    },
    {
      accessorKey: 'status',
      header: () => t('STATUS'),
      cell: formatStatusCell,
    },
    {
      accessorKey: 'laneRatesUpdatedAt',
      header: () => <div className="min-w-24">{t('UPDATED')}</div>,
      cell: formatUpdatedAtCell,
    },
    {
      accessorKey: 'laneRatesCreatedAt',
      header: ({ column }) => {
        return (
          <SortableColumn column={column} className="min-w-24">
            {t('CREATED')}
          </SortableColumn>
        );
      },
      cell: formatCreatedAtCell,
    },
  ];
};
